import { ColumnDef } from "@tanstack/react-table";
import { AddonReturn } from "@/types/facility/addon-returns";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { displayDate, convert24HourTo12Hour } from "@/lib/helper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Edit2, X } from "lucide-react";

interface EditableCellProps {
  value: number;
  isEditing: boolean;
  onChange: (val: number) => void;
}

const EditableCell = ({ value, isEditing, onChange }: EditableCellProps) => {
  if (!isEditing) {
    return <span>{value}</span>;
  }

  return (
    <Input
      type="number"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-20 h-8"
      autoFocus
    />
  );
};

export const getColumns = (
  editingRowId: number | null,
  editingQuantity: number,
  setEditingRowId: (id: number | null) => void,
  setEditingQuantity: (qty: number) => void,
  handleSave: (bookingId: number, addonId: number, quantity: number) => Promise<void>,
  searchCriteria: { sort_key: string; sort_order: string },
  toggleSortOrder: (key: string, currentOrder: string) => void
): ColumnDef<AddonReturn>[] => {
  const addonReturnsPermission = (() => {
    try {
      const accessLevels = JSON.parse(localStorage.getItem("accessLevels") as string);
      return accessLevels.addon_returns ?? "no_access";
    } catch {
      return "no_access";
    }
  })();

  const columns: ColumnDef<AddonReturn>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Name"
          sortKey="name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
    },
    {
      accessorKey: "member_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Member Name"
          sortKey="member_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => <span className="capitalize">{row.original.member_name || "-"}</span>,
    },
    {
      accessorKey: "booking_date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Booking Date"
          sortKey="booking_date"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => displayDate(row.original.booking_date),
    },
    {
      accessorKey: "start_time",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Start Time"
          sortKey="start_time"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => convert24HourTo12Hour(row.original.start_time),
    },
    {
      accessorKey: "end_time",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="End Time"
          sortKey="end_time"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => convert24HourTo12Hour(row.original.end_time),
    },
    {
      accessorKey: "max_attendees",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Issued Quantity"
          sortKey="max_attendees"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => row.original.max_attendees,
    },
    {
      accessorKey: "returned_quantity",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Returned Quantity"
          sortKey="returned_quantity"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const isEditing = editingRowId === row.original.id;
        return (
          <EditableCell
            value={isEditing ? editingQuantity : row.original.returned_quantity}
            isEditing={isEditing}
            onChange={setEditingQuantity}
          />
        );
      },
    },
    {
      accessorKey: "remaining_quantity",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Remaining Quantity"
          sortKey="remaining_quantity"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => row.original.remaining_quantity,
    },
  ];

  if (addonReturnsPermission === "full_access") {
    columns.push({
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const isEditing = editingRowId === row.original.id;

        return (
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                  onClick={() => handleSave(row.original.booking_id, row.original.add_on_id, editingQuantity)}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setEditingRowId(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => {
                  setEditingRowId(row.original.id);
                  setEditingQuantity(row.original.returned_quantity);
                }}
              >
                <Edit2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
    });
  }

  return columns;
};